import { Outlet, useLocation } from 'react-router-dom';

import { useNavigate } from '../router';
import AuthWrapper from '../components/auth/AuthWrapper.tsx';
import React, { useEffect } from 'react';
import { Auth0Provider } from '@auth0/auth0-react';
import { ExportProvider } from '../contexts/ExportContext';
import { PushProvider } from '@hooks/usePush/PushContext.tsx';
import { JobEventsProvider } from '@hooks/useJobs/JobEventsContext.tsx';

import * as Sentry from '@sentry/react';
import { isProduction } from '@utils/utils.tsx';
import ExportStatusBanner from '@components/export/ExportStatusBanner';
import { SecurityModeProvider } from '@components/security/SecurityModeContext';
import SecurityOverlay from '@components/security/SecurityOverlay';
import { createLogger } from '@utils/logger';
import { CalendarLayoutProvider } from '@components/features/calendar-layout/CalendarLayoutProvider';
import { AppMessagesProvider } from '@components/appMessages/AppMessagesProvider';
import { TableFilterFavouritesProvider } from '@components/RingilFilters/v2/TableFilterFavourites';
import AssistantProvider from '@components/ringil3/Features/Assistant/AssistantProvider.tsx';

const log = createLogger('app');

if (isProduction()) {
  Sentry.init({
    dsn: 'https://d8c638f05cd024570133929e49c5b54e@o4508126704893952.ingest.de.sentry.io/4508126706597968',
    // Tag every event with the deployed commit SHA (same value served in /version.json) so issues are
    // attributable to a specific build. This lets us tell whether an error is still happening on the
    // CURRENT release or only lingering in stale, cached browser sessions running an old bundle — and
    // lets already-shipped fixes auto-resolve via "resolved in the next release". Injected at build
    // time from $GITHUB_SHA (see DockerfileGithub.dockerfile / production.yml). Undefined in local dev,
    // where Sentry isn't initialized anyway.
    release: import.meta.env.VITE_COMMIT_SHA || 'unknown',
    // Deploy environment (production / alpha / playground …), injected from the workflow's NAMESPACE at
    // build time. Falls back to 'production' since Sentry is currently only initialized in prod.
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? 'unknown',
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    ignoreErrors: [
      // Drop unactionable third-party noise: a throttled label-collision pass inside the HERE Maps
      // engine (mapsjs-core.js) fires after the map is disposed on unmount and reads `_view` on null.
      // The map is already gone, so it's harmless — and there's no first-party variable to guard.
      // (Sentry RINGIL-APP-A3.)
      /lookAtManipulator/,
      // AbortError from requests aborted on unmount/navigation (e.g. the version.json poller). This is
      // expected control flow across the whole app, never a real bug. (Sentry RINGIL-APP-E5.)
      /AbortError/,
      /signal is aborted/i,
      // Script injected by some mobile in-app browsers (seen only in Seznam prohlížeč on Android)
      // runs its own document-level touchmove handler and derefs a null touch object. The whole
      // stack is `<anonymous>` — no app frame is involved. (Sentry RINGIL-APP-QX.)
      /reading 'touches'/,
      // Firefox's window.onerror wording for a bare `throw undefined`: no stack, no line, reported
      // against the page URL. Firefox-only across 18 months and on every route, i.e. extension or
      // browser noise — our own crashes always arrive as a typed Error with frames, and a stackless
      // throw would be unactionable anyway. (Sentry RINGIL-APP-BS.)
      /uncaught exception: undefined/,
      // WKWebView bridge probe injected by an iOS in-app browser (Seznam prohlížeč): its
      // `sendToBrowser` runs in the document scope and derefs window.webkit.messageHandlers when
      // the native bridge is absent. Not our code — index.html ships no inline script.
      // (Sentry RINGIL-APP-Q6.)
      /window\.webkit\.messageHandlers/,
    ],
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ['localhost', /^https:\/\/yourserver\.io\/api/],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
}

export default function App() {
  const navigate = useNavigate();
  const location = useLocation();

  const domain = import.meta.env.VITE_AUTH0_DOMAIN;
  const clientId = import.meta.env.VITE_AUTH0_CLIENTID;
  // Optional API audience. Only sent when configured (per environment / .env.local).
  // Required by some Auth0 tenants for refresh tokens to be issued.
  const audience = import.meta.env.VITE_AUTH0_AUDIENCE;

  log.debug('Settings', { domain, clientId, audience });

  // Kontrola, zda je cesta veřejná
  const isPublicPath = location.pathname.startsWith('/public') || location.pathname === '/maintenance';

  // Pokud je cesta veřejná, nepoužívá Auth0Provider
  if (isPublicPath) {
    return <Outlet />;
  }

  // Google Analytics initialization function with DNT and GPC respect
  function initializeGoogleAnalytics() {
    const dnt = navigator.doNotTrack === "1" || window.doNotTrack === "1" || navigator.msDoNotTrack === "1";
    const gpc = navigator.globalPrivacyControl === true;
    if (!dnt && !gpc) {
      if (!window.gtagInitialized) {
        window.dataLayer = window.dataLayer || [];
        window.gtag = function() { window.dataLayer!.push(arguments); };
        const gaScript = document.createElement('script');
        gaScript.async = true;
        gaScript.src = "https://www.googletagmanager.com/gtag/js?id=G-7YHPJNNR21";
        gaScript.onload = () => {
          window.gtag('js', new Date());
          window.gtag('config', 'G-7YHPJNNR21');
          window.gtagInitialized = true;
          log.debug('Google Analytics script loaded and initialized', { href: window.location.href });
        };
        gaScript.onerror = (e) => {
          console.error('Failed to load Google Analytics script', e);
        };
        document.head.appendChild(gaScript);
        log.debug('Google Analytics script tag added to DOM');
      } else {
        log.debug('Google Analytics already initialized.');
      }
    } else {
      log.debug('Google Analytics NOT initialized due to DNT or GPC', { dnt, gpc });
    }
  }

  // Only call GA initialization after Auth0 redirect, or on initial mount if not redirected
  useEffect(() => {
    if (!isPublicPath) {
      initializeGoogleAnalytics();
    }
  }, [isPublicPath]);

  return (
    <>
      <Auth0Provider
        domain={domain}
        clientId={clientId}
        useRefreshTokens={true}
        authorizationParams={{
          redirect_uri: window.location.origin,
          // offline_access is required for Auth0 to issue a refresh token, which the API
          // layer uses to silently renew an expired identity token and retry the request.
          scope: 'openid profile email offline_access',
          ...(audience ? { audience } : {}),
        }}
        onRedirectCallback={(appState) => {
          // Remove query params (OAuth code) from URL after redirect
          const cleanUrl = window.location.pathname + window.location.hash;
          window.history.replaceState({}, document.title, cleanUrl);
          // Initialize Google Analytics after URL is cleaned
          initializeGoogleAnalytics();
          // @ts-expect-error - navigate očekává Path type z routeru
          navigate(appState.returnTo);
        }}
      >
        <AuthWrapper>
          <SecurityModeProvider>
            <PushProvider>
              <JobEventsProvider>
                <ExportProvider>
                  <ExportStatusBanner />
                  {/* Above the outlet on purpose: the version baseline must outlive navigation, or the
                  new-version notice re-baselines itself away and users stay on the old bundle. */}
              <AppMessagesProvider>
                {/* App-wide so the calendar and the layout designer share one instance: saving in the
                    designer updates the calendar without a reload. */}
                <CalendarLayoutProvider>
                  {/* App-wide so every filtered table shares one bootstrap call for the user's
                      favourite (pinned) filters. */}
                  <TableFilterFavouritesProvider>
                    {/* Above the outlet: the panel must outlive navigation, or it closes itself
                        every time the user follows a step the assistant described. */}
                    <AssistantProvider>
                      <Outlet />
                    </AssistantProvider>
                  </TableFilterFavouritesProvider>
                </CalendarLayoutProvider>
              </AppMessagesProvider>
                </ExportProvider>
              </JobEventsProvider>
            </PushProvider>
            <SecurityOverlay />
          </SecurityModeProvider>
        </AuthWrapper>
      </Auth0Provider>
    </>
  );
}
